import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts'

interface PieChartComponentProps {
  data: Array<Record<string, any>>
  dataKey: string
  nameKey: string
  title?: string
  showPercentage?: boolean
  innerRadius?: number // 0 = pie completo, >0 = donut
  colors?: string[]
  loading?: boolean
}

// Paleta de colores por defecto
const DEFAULT_COLORS = [
  '#10b981', // Verde
  '#3b82f6', // Azul
  '#f59e0b', // Naranja
  '#ef4444', // Rojo
  '#8b5cf6', // Morado
  '#ec4899', // Rosa
  '#14b8a6', // Teal
  '#f97316', // Naranja oscuro
  '#06b6d4', // Cyan
  '#84cc16', // Lima
]

export function PieChartComponent({
  data,
  dataKey,
  nameKey,
  title,
  showPercentage = true,
  innerRadius = 0,
  colors = DEFAULT_COLORS,
  loading = false,
}: PieChartComponentProps) {
  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[350px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[350px] flex items-center justify-center text-gray-500">
          No hay datos disponibles
        </div>
      </div>
    )
  }

  // Transformar datos para asegurar tipos correctos
  const chartData = data.map((item) => ({
    name: String(item[nameKey] || 'Sin nombre'),
    value: Number(item[dataKey]) || 0,
  }))

  // Calcular total para porcentajes
  const total = chartData.reduce((sum, item) => sum + item.value, 0)

  // Custom label para mostrar porcentajes
  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }: any) => {
    if (!showPercentage || percent < 0.05) return null // No mostrar si < 5%

    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: '12px', fontWeight: 'bold' }}
      >
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    )
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      const percentage = total > 0 ? ((data.value / total) * 100).toFixed(2) : '0.00'

      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900">{data.name}</p>
          <p className="text-sm text-gray-600 mt-1">
            Valor: <span className="font-medium">{data.value.toLocaleString('es-ES')}</span>
          </p>
          <p className="text-sm text-gray-600">
            Porcentaje: <span className="font-medium">{percentage}%</span>
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <div className="bg-white border rounded-lg p-6">
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}

      <ResponsiveContainer width="100%" height={350}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={120}
            fill="#8884d8"
            label={showPercentage ? renderCustomLabel : false}
            labelLine={false}
          >
            {chartData.map((_entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            iconType="circle"
            formatter={(value) => {
              const index = chartData.findIndex((d) => d.name === value)
              if (index === -1) return value

              const pct =
                total > 0 ? ((chartData[index].value / total) * 100).toFixed(1) : '0.0'
              return `${value} (${pct}%)`
            }}
            wrapperStyle={{
              fontSize: '12px',
              maxHeight: '300px',
              overflowY: 'auto',
            }}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Resumen total */}
      <div className="mt-4 pt-4 border-t border-gray-200 text-center">
        <p className="text-sm text-gray-600">
          Total: <span className="font-semibold text-gray-900">{total.toLocaleString('es-ES')}</span>
        </p>
      </div>
    </div>
  )
}
